import { aboutInfo } from '@/mocks/shows';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  // Schema
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于屁屁影院',
        description: aboutInfo.brandIntro,
        url: 'https://pipicinema.com/about',
        isPartOf: { '@type': 'WebSite', name: '屁屁影院', url: 'https://pipicinema.com' },
      },
      {
        '@type': 'Organization',
        name: '屁屁影院',
        url: 'https://pipicinema.com',
        description: aboutInfo.brandIntro,
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutInfo.contact.email,
          contactType: 'customer service',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://pipicinema.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://pipicinema.com/about' },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <div className="pt-20 md:pt-24 pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* ===== Hero ===== */}
          <section className="relative rounded-2xl overflow-hidden h-[240px] md:h-[320px] mb-12 mt-4">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20theater%20with%20large%20screen%20displaying%20colorful%20film%20frames%2C%20comfortable%20seating%2C%20cinematic%20ambient%20lighting%20with%20warm%20amber%20glow%2C%20stylized%20illustration%20with%20rich%20contrast%2C%20inviting%20entertainment%20atmosphere&width=1600&height=640&seq=about-hero-pipi&orientation=landscape"
              alt="屁屁影院 - 关于我们"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-foreground-950/55 flex flex-col items-center justify-center px-4">
              <h1 className="font-heading text-2xl md:text-4xl text-background-50 mb-3">
                关于屁屁影院
              </h1>
              <p className="text-sm md:text-base text-background-50/70 max-w-lg text-center">
                让每一个人都能免费享受高品质的影视内容
              </p>
            </div>
          </section>

          {/* ===== Brand Intro ===== */}
          <section id="intro" className="mb-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">品牌介绍</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {aboutInfo.brandIntro}
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                我们始终秉持<strong>{aboutInfo.mission}</strong>的理念，不断优化产品体验，让追剧成为一件简单快乐的事。
              </p>
            </div>
          </section>

          {/* ===== Stats ===== */}
          <section id="stats" className="mb-12">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-xl p-5 md:p-6 text-center"
                >
                  <span className="block font-heading text-2xl md:text-3xl text-primary-500 mb-1">
                    {stat.value}
                  </span>
                  <span className="text-sm text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ===== Values ===== */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutInfo.values.map((v) => (
                <div
                  key={v.title}
                  className="bg-background-100 rounded-xl p-5 md:p-6"
                >
                  <h3 className="font-heading text-base text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ===== Contact ===== */}
          <section id="contact" className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6 text-center">
              联系我们
            </h2>
            <div className="max-w-xl mx-auto bg-background-100 rounded-xl p-6 md:p-8">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">电子邮箱</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">微信公众号</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg flex-shrink-0">
                    <i className="ri-weibo-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">微博</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}